import { type FormEvent, useEffect, useState } from 'react'
import { api, type Buchung, type Dauerauftrag, type DauerauftragEingabe, type Konto, type Periode } from '../api'
import { betrag, betragAlsEingabe, betragEingabe, datum, heute, periodeText, typText } from '../format'
import { Dialog, Fehlermeldung, fehlerText, KontoAuswahl, kontoArten, mitTyp } from './gemeinsam'

export function DauerauftragFormular({
  start,
  konten,
  onSpeichern,
}: {
  start: DauerauftragEingabe
  konten: Konto[]
  onSpeichern: (e: DauerauftragEingabe) => Promise<void>
}) {
  const [e, setE] = useState(start)
  const [fehler, setFehler] = useState<string | null>(null)
  const setze = <K extends keyof DauerauftragEingabe>(k: K, v: DauerauftragEingabe[K]) => setE((alt) => ({ ...alt, [k]: v }))
  const arten = kontoArten(e.buchungstyp)

  async function absenden(ev: FormEvent) {
    ev.preventDefault()
    const wert = betragEingabe(e.wert)
    if (wert === null) return setFehler('Bitte einen gültigen Betrag eingeben.')
    try {
      await onSpeichern({ ...e, wert, ende: e.ende || null })
    } catch (err) {
      setFehler(fehlerText(err))
    }
  }

  return (
    <form className="formular" onSubmit={absenden}>
      <fieldset className="radios">
        {(Object.keys(periodeText) as Periode[]).map((p) => (
          <label key={p}>
            <input type="radio" checked={e.periode === p} onChange={() => setze('periode', p)} /> {periodeText[p]}
          </label>
        ))}
      </fieldset>
      <fieldset className="radios">
        {(['AUSGANG', 'EINGANG'] as const).map((t) => (
          <label key={t}>
            <input type="radio" checked={e.buchungstyp === t} onChange={() => setE((alt) => mitTyp(alt, 'buchungstyp', t, konten))} /> {typText[t]}
          </label>
        ))}
      </fieldset>
      <label>
        Name
        <input maxLength={200} value={e.name} onChange={(ev) => setze('name', ev.target.value)} />
      </label>
      <label>
        Beschreibung
        <input maxLength={200} value={e.beschreibung} onChange={(ev) => setze('beschreibung', ev.target.value)} />
      </label>
      <label>
        Betrag
        <input inputMode="decimal" required value={e.wert} onChange={(ev) => setze('wert', ev.target.value)} />
      </label>
      <div className="nebeneinander">
        <label>
          Erste Ausführung
          <input type="date" required value={e.start} onChange={(ev) => setze('start', ev.target.value)} />
        </label>
        <label>
          Ende (optional)
          <input type="date" value={e.ende ?? ''} onChange={(ev) => setze('ende', ev.target.value || null)} />
        </label>
      </div>
      <label>
        Von Konto
        <KontoAuswahl konten={konten} art={arten.konto} wert={e.kontoId} onChange={(id) => setze('kontoId', id)} />
      </label>
      <label>
        An Gegenkonto
        <KontoAuswahl konten={konten} art={arten.gegenkonto} wert={e.gegenkontoId} onChange={(id) => setze('gegenkontoId', id)} />
      </label>
      <p className="hinweis">Beim Speichern werden alle bis heute fälligen Termine gebucht.</p>
      <Fehlermeldung fehler={fehler} />
      <div className="knoepfe">
        <button type="submit" className="primaer">
          Speichern
        </button>
      </div>
    </form>
  )
}

function alsEingabe(d: Dauerauftrag): DauerauftragEingabe {
  return {
    periode: d.periode,
    name: d.name,
    beschreibung: d.beschreibung,
    wert: betragAlsEingabe(d.wert),
    buchungstyp: d.buchungstyp,
    start: d.start,
    ende: d.ende,
    kontoId: d.kontoId,
    gegenkontoId: d.gegenkontoId,
  }
}

const neu: () => DauerauftragEingabe = () => ({
  periode: 'MONATLICH',
  name: '',
  beschreibung: '',
  wert: '',
  buchungstyp: 'AUSGANG',
  start: heute(),
  ende: null,
  kontoId: null,
  gegenkontoId: null,
})

export function Dauerauftraege() {
  const [liste, setListe] = useState<Dauerauftrag[]>([])
  const [konten, setKonten] = useState<Konto[]>([])
  const [bearbeiten, setBearbeiten] = useState<{ id: number | null; eingabe: DauerauftragEingabe } | null>(null)
  const [details, setDetails] = useState<{ da: Dauerauftrag; buchungen: Buchung[] } | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = () => api.dauerauftraege().then(setListe).catch((e) => setFehler(fehlerText(e)))
  useEffect(() => {
    laden()
    api.konten().then(setKonten).catch((e) => setFehler(fehlerText(e)))
  }, [])

  async function loeschen(d: Dauerauftrag) {
    if (!confirm(`Dauerauftrag „${d.name}“ löschen? Bereits erzeugte Buchungen bleiben erhalten.`)) return
    try {
      await api.dauerauftragLoeschen(d.id)
      await laden()
    } catch (e) {
      setFehler(fehlerText(e))
    }
  }

  async function zeigen(d: Dauerauftrag) {
    try {
      setDetails({ da: d, buchungen: await api.dauerauftragBuchungen(d.id) })
    } catch (e) {
      setFehler(fehlerText(e))
    }
  }

  return (
    <section className="karte">
      <div className="werkzeuge">
        <h2>Daueraufträge</h2>
        <button className="primaer" onClick={() => setBearbeiten({ id: null, eingabe: neu() })}>
          Neuer Dauerauftrag
        </button>
      </div>
      <Fehlermeldung fehler={fehler} />
      <table>
        <thead>
          <tr>
            <th>Periode</th>
            <th>Name</th>
            <th>Beschreibung</th>
            <th className="zahl">Betrag</th>
            <th>Typ</th>
            <th>Von → An</th>
            <th>Start</th>
            <th>Ende</th>
            <th>Zuletzt</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {liste.map((d) => (
            <tr key={d.id}>
              <td>{periodeText[d.periode]}</td>
              <td>{d.name}</td>
              <td>{d.beschreibung}</td>
              <td className="zahl">{betrag(d.wert)}</td>
              <td>{typText[d.buchungstyp]}</td>
              <td>
                {d.kontoBezeichnung ?? '–'} → {d.gegenkontoBezeichnung ?? '–'}
              </td>
              <td>{datum(d.start)}</td>
              <td>{datum(d.ende)}</td>
              <td>{datum(d.letzteBuchung)}</td>
              <td className="aktionen">
                <button className="link" onClick={() => zeigen(d)}>
                  Buchungen
                </button>
                <button className="link" onClick={() => setBearbeiten({ id: d.id, eingabe: alsEingabe(d) })}>
                  Bearbeiten
                </button>
                <button className="link gefahr" onClick={() => loeschen(d)}>
                  Löschen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog offen={bearbeiten != null} titel={bearbeiten?.id ? 'Dauerauftrag bearbeiten' : 'Neuer Dauerauftrag'} onSchliessen={() => setBearbeiten(null)}>
        {bearbeiten && (
          <DauerauftragFormular
            start={bearbeiten.eingabe}
            konten={konten}
            onSpeichern={async (e) => {
              if (bearbeiten.id) await api.dauerauftragAendern(bearbeiten.id, e)
              else await api.dauerauftragAnlegen(e)
              setBearbeiten(null)
              await laden()
            }}
          />
        )}
      </Dialog>

      <Dialog offen={details != null} titel={`Buchungen: ${details?.da.name ?? ''}`} onSchliessen={() => setDetails(null)}>
        {details && (
          <table>
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Datum</th>
                <th className="zahl">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {details.buchungen.map((b) => (
                <tr key={b.id}>
                  <td>{b.buchungsnummer}</td>
                  <td>{datum(b.datum)}</td>
                  <td className="zahl">{betrag(b.wert)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Dialog>
    </section>
  )
}
